import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Card,
  Button,
  Spinner,
  Alert,
  Pagination,
  Row,
  Col,
  Badge,
  Form,
  InputGroup,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

function Locations() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [stockItems, setStockItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockError, setStockError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [summaryStats, setSummaryStats] = useState({
    totalLocations: 0,
    totalStockValue: 0,
    totalStockCost: 0,
    totalItems: 0,
    lowStockItems: 0,
  });

  // Add these state variables
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    loc_product_name: "",
    loc_product_quantity: 0,
    loc_product_wp: 0,
    loc_product_sp: 0,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  // Adjustment Modal States
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [adjustFormData, setAdjustFormData] = useState({
    adjustment_type: "add", // add, subtract, set
    adjustment_quantity: 0,
    reason: "",
  });
  const [savingAdjustment, setSavingAdjustment] = useState(false);
  const [adjustError, setAdjustError] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/locations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch locations");
      }

      const data = await response.json();
      setLocations(data.locations);

      // Set first location as selected by default if available
      if (data.locations && data.locations.length > 0 && !selectedLocation) {
        setSelectedLocation(data.locations[0]);
      }

      // Calculate summary stats
      setSummaryStats({
        totalLocations: data.locations?.length || 0,
        ...data.summary,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setEditFormData({
      loc_product_name: item.loc_product_name || "",
      loc_product_quantity: item.loc_product_quantity || 0,
      loc_product_wp: item.loc_product_wp || 0,
      loc_product_sp: item.loc_product_sp || 0,
      loc_product_min_quantity: item.loc_product_min_quantity || 0,
      loc_product_max_quantity: item.loc_product_max_quantity || 0,
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]:
        name.includes("quantity") || name.includes("wp") || name.includes("sp")
          ? parseFloat(value) || 0
          : value,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    setSavingEdit(true);
    setEditError(null);

    try {
      const response = await fetch(
        `${API_URL}/location-stock/${editingItem._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ...editFormData,
            location_id: editingItem.location_id,
            loc_product_id: editingItem.loc_product_id,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update stock item");
      }

      const updatedItem = await response.json();

      // Update the stockItems array with the edited item
      setStockItems((prevItems) =>
        prevItems.map((item) =>
          item._id === editingItem._id ? updatedItem : item,
        ),
      );

      setShowEditModal(false);
      setEditingItem(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };
  const fetchLocationStock = async (locationId, page = 1, search = "") => {
    if (!locationId) return;

    setStockLoading(true);
    setStockError(null);
    try {
      const url = `${API_URL}/location-stock/${locationId}?page=${page}&limit=10${search ? `&search=${encodeURIComponent(search)}` : ""}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch location stock");
      }

      const data = await response.json();
      setStockItems(data.stockItems);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
      setCurrentPage(data.currentPage);
    } catch (err) {
      setStockError(err.message);
    } finally {
      setStockLoading(false);
    }
  };

  const handleLocationChange = (locationId) => {
    const location = locations.find((loc) => loc.location_id === locationId);
    setSelectedLocation(location);
    setCurrentPage(1);
    setSearchTerm("");
    fetchLocationStock(locationId, 1, "");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedLocation) {
      setCurrentPage(1);
      fetchLocationStock(selectedLocation.location_id, 1, searchTerm);
    }
  };

  // Adjustment Handlers
  const handleAdjustClick = (item) => {
    setAdjustingItem(item);
    setAdjustFormData({
      adjustment_type: "add",
      adjustment_quantity: 0,
      reason: "",
    });
    setAdjustError(null);
    setShowAdjustModal(true);
  };

  const handleAdjustFormChange = (e) => {
    const { name, value } = e.target;
    setAdjustFormData((prev) => ({
      ...prev,
      [name]: name === "adjustment_quantity" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!adjustingItem) return;

    if (adjustFormData.adjustment_quantity <= 0) {
      setAdjustError("Adjustment quantity must be greater than 0");
      return;
    }

    if (!adjustFormData.reason.trim()) {
      setAdjustError("Please provide a reason for the adjustment");
      return;
    }

    setSavingAdjustment(true);
    setAdjustError(null);

    try {
      let newQuantity = adjustingItem.loc_product_quantity;

      switch (adjustFormData.adjustment_type) {
        case "add":
          newQuantity += adjustFormData.adjustment_quantity;
          break;
        case "subtract":
          newQuantity -= adjustFormData.adjustment_quantity;
          if (newQuantity < 0) newQuantity = 0;
          break;
        case "set":
          newQuantity = adjustFormData.adjustment_quantity;
          break;
        default:
          break;
      }

      const response = await fetch(
        `${API_URL}/location-stock/${adjustingItem._id}/adjust`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            adjustment_type: adjustFormData.adjustment_type,
            adjustment_quantity: adjustFormData.adjustment_quantity,
            new_quantity: newQuantity,
            reason: adjustFormData.reason,
            location_id: adjustingItem.location_id,
            loc_product_id: adjustingItem.loc_product_id,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to adjust stock");
      }

      const updatedItem = await response.json();

      setStockItems((prevItems) =>
        prevItems.map((item) =>
          item._id === adjustingItem._id ? updatedItem : item,
        ),
      );

      setShowAdjustModal(false);
      setAdjustingItem(null);

      // Show success message
      const adjustmentMessage =
        adjustFormData.adjustment_type === "add"
          ? "added"
          : adjustFormData.adjustment_type === "subtract"
            ? "subtracted"
            : "set to";

      alert(
        `Successfully ${adjustmentMessage} ${adjustFormData.adjustment_quantity} units from ${adjustingItem.loc_product_name}`,
      );
    } catch (err) {
      setAdjustError(err.message);
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (selectedLocation) {
      fetchLocationStock(selectedLocation.location_id, newPage, searchTerm);
    }
  };

  useEffect(() => {
    if (!token || !user) {
      navigate("/login");
      return;
    }
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationStock(selectedLocation.location_id, currentPage, searchTerm);
    }
  }, [selectedLocation]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-KE").format(num || 0);
  };

  const getStockStatusBadge = (current, min, max) => {
    if (current <= 0) {
      return <Badge bg="danger">Out of Stock</Badge>;
    } else if (current <= min) {
      return (
        <Badge bg="warning" text="dark">
          Low Stock
        </Badge>
      );
    } else if (current >= max) {
      return <Badge bg="success">Over Stock</Badge>;
    } else {
      return <Badge bg="info">Normal</Badge>;
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading locations...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Locations & Stock Management</h1>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={fetchLocations} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "Refresh"}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 bg-primary text-white">
            <Card.Body>
              <Card.Title className="text-white-50">Total Locations</Card.Title>
              <h2>{formatNumber(summaryStats.totalLocations)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 bg-success text-white">
            <Card.Body>
              <Card.Title className="text-white-50">
                Total Stock Value
              </Card.Title>
              <h2>{formatCurrency(summaryStats.totalStockValue)}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 bg-info text-white">
            <Card.Body>
              <Card.Title className="text-white-50">Total Cost</Card.Title>
              <h2>{formatCurrency(summaryStats.totalStockCost)}</h2>
            </Card.Body>
          </Card>
        </Col>
        {/* <Col md={3} sm={6} className="mb-3">
          <Card className="h-100 bg-warning text-white">
            <Card.Body>
              <Card.Title className="text-white-50">Low Stock Items</Card.Title>
              <h2>{formatNumber(summaryStats.lowStockItems)}</h2>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>

      {/* Location Selection */}
      {locations.length > 0 ? (
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Select Location</Form.Label>
                  <Form.Select
                    value={selectedLocation?.location_id || ""}
                    onChange={(e) => handleLocationChange(e.target.value)}
                  >
                    <option value="">Choose a location...</option>
                    {locations.map((location) => (
                      <option
                        key={location.location_id}
                        value={location.location_id}
                      >
                        {location.location_name}{" "}
                        {location.pos_location ? "(POS)" : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                {selectedLocation && (
                  <Card className="bg-light">
                    <Card.Body>
                      <Row>
                        <Col xs={6}>
                          <small className="text-muted d-block">Branch</small>
                          <strong>{selectedLocation.branch_name}</strong>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="warning">No locations found</Alert>
      )}

      {/* Stock Search */}
      {selectedLocation && (
        <Card className="mb-4">
          <Card.Body>
            <Form onSubmit={handleSearch}>
              <Row>
                <Col md={8}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search by product name, code, or category..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button type="submit" variant="primary">
                      Search
                    </Button>
                  </InputGroup>
                </Col>
                <Col md={4} className="text-md-end mt-2 mt-md-0">
                  <Badge bg="secondary" className="p-2">
                    Total Items: {formatNumber(totalItems)}
                  </Badge>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Stock Table */}
      {selectedLocation && (
        <>
          {stockLoading && stockItems.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading stock items...</p>
            </div>
          ) : stockError ? (
            <Alert variant="danger">{stockError}</Alert>
          ) : stockItems.length === 0 ? (
            <Alert variant="info">No stock items found for this location</Alert>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="d-none d-lg-block">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Product Code</th>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th>Wholesale Price</th>

                      <th>Selling Price</th>
                      <th>Total Selling Price</th>
                      <th>Status</th>
                      <th>Edited</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.map((item) => (
                      <tr key={item._id}>
                        <td className="align-middle">
                          <strong>{item.loc_product_code}</strong>
                        </td>
                        <td className="align-middle">
                          {item.loc_product_name}
                        </td>
                        <td className="align-middle text-center">
                          {formatNumber(item.loc_product_quantity)}
                        </td>
                        <td className="align-middle">
                          {formatCurrency(item.loc_product_wp)}
                        </td>
                        <td className="align-middle">
                          {formatCurrency(item.loc_product_sp)}
                        </td>
                        <td className="align-middle fw-bold text-success">
                          {formatCurrency(item.total_value)}
                        </td>
                        <td className="align-middle">
                          {getStockStatusBadge(
                            item.loc_product_quantity,
                            item.loc_product_min_quantity,
                            item.loc_product_max_quantity,
                          )}
                        </td>
                        <td className="align-middle text-center">
                          {item.updated ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="secondary">No</Badge>
                          )}
                        </td>
                        <td className="align-middle">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditClick(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleAdjustClick(item)}
                          >
                            Adjust
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="d-lg-none">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Product Name</th>
                      <th>Quantity</th>
                      <th>W/P</th>

                      <th>S/P</th>
                      <th>Total S/P</th>
                      <th>Status</th>
                      <th>Edited</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockItems.map((item) => (
                      <tr key={item._id}>
                        <td className="align-middle">
                          <strong>{item.loc_product_code}</strong>
                        </td>
                        <td className="align-middle">
                          {item.loc_product_name}
                        </td>
                        <td className="align-middle text-center">
                          {formatNumber(item.loc_product_quantity)}
                        </td>
                        <td className="align-middle">
                          {formatCurrency(item.loc_product_wp)}
                        </td>
                        <td className="align-middle">
                          {formatCurrency(item.loc_product_sp)}
                        </td>
                        <td className="align-middle fw-bold text-success">
                          {formatCurrency(item.total_value)}
                        </td>
                        <td className="align-middle">
                          {getStockStatusBadge(
                            item.loc_product_quantity,
                            item.loc_product_min_quantity,
                            item.loc_product_max_quantity,
                          )}
                        </td>
                        <td className="align-middle text-center">
                          {item.updated ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="secondary">No</Badge>
                          )}
                        </td>
                        <td className="align-middle">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditClick(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => handleAdjustClick(item)}
                          >
                            Adjust
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="text-muted">
                    Showing page {currentPage} of {totalPages} ({totalItems}{" "}
                    total items)
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Pagination.Item
                          key={i}
                          active={pageNum === currentPage}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Stock Item</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            {editError && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setEditError(null)}
              >
                {editError}
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="loc_product_name"
                    value={editFormData.loc_product_name}
                    onChange={handleEditFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingItem?.loc_product_code || ""}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    name="loc_product_quantity"
                    value={editFormData.loc_product_quantity}
                    onChange={handleEditFormChange}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Wholesale Price (W/P)</Form.Label>
                  <Form.Control
                    type="number"
                    name="loc_product_wp"
                    value={editFormData.loc_product_wp}
                    onChange={handleEditFormChange}
                    min="0"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Selling Price (S/P)</Form.Label>
                  <Form.Control
                    type="number"
                    name="loc_product_sp"
                    value={editFormData.loc_product_sp}
                    onChange={handleEditFormChange}
                    min="0"
                    step="0.01"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>
                  <Form.Control
                    type="text"
                    value={editingItem?.category_name || ""}
                    disabled
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr />
            <div className="bg-light p-3 rounded">
              <h6>Calculated Totals:</h6>
              <Row>
                <Col md={4}>
                  <small className="text-muted">Total Cost:</small>
                  <div className="fw-bold">
                    {formatCurrency(
                      editFormData.loc_product_quantity *
                        (editingItem?.loc_product_bp || 0),
                    )}
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Total Wholesale:</small>
                  <div className="fw-bold">
                    {formatCurrency(
                      editFormData.loc_product_quantity *
                        editFormData.loc_product_wp,
                    )}
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Total Selling:</small>
                  <div className="fw-bold text-success">
                    {formatCurrency(
                      editFormData.loc_product_quantity *
                        editFormData.loc_product_sp,
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal show={showAdjustModal} onHide={() => setShowAdjustModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Adjust Stock Quantity</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdjustSubmit}>
          <Modal.Body>
            {adjustError && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setAdjustError(null)}
              >
                {adjustError}
              </Alert>
            )}

            <div className="mb-3">
              <strong>Product:</strong> {adjustingItem?.loc_product_name} (
              {adjustingItem?.loc_product_code})
            </div>
            <div className="mb-3">
              <strong>Current Quantity:</strong>{" "}
              {formatNumber(adjustingItem?.loc_product_quantity || 0)}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Adjustment Type</Form.Label>
              <Form.Select
                name="adjustment_type"
                value={adjustFormData.adjustment_type}
                onChange={handleAdjustFormChange}
                required
              >
                <option value="add">Add Quantity (+)</option>
                <option value="subtract">Subtract Quantity (-)</option>
                <option value="set">Set to Exact Quantity (=)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {adjustFormData.adjustment_type === "add"
                  ? "Quantity to Add"
                  : adjustFormData.adjustment_type === "subtract"
                    ? "Quantity to Subtract"
                    : "New Quantity"}
              </Form.Label>
              <Form.Control
                type="number"
                name="adjustment_quantity"
                value={adjustFormData.adjustment_quantity}
                onChange={handleAdjustFormChange}
                min="0"
                step="1"
                required
              />
              {adjustFormData.adjustment_type === "add" && (
                <Form.Text className="text-muted">
                  New quantity will be:{" "}
                  {formatNumber(
                    (adjustingItem?.loc_product_quantity || 0) +
                      adjustFormData.adjustment_quantity,
                  )}
                </Form.Text>
              )}
              {adjustFormData.adjustment_type === "subtract" && (
                <Form.Text className="text-muted">
                  New quantity will be:{" "}
                  {formatNumber(
                    Math.max(
                      0,
                      (adjustingItem?.loc_product_quantity || 0) -
                        adjustFormData.adjustment_quantity,
                    ),
                  )}
                </Form.Text>
              )}
              {adjustFormData.adjustment_type === "set" && (
                <Form.Text className="text-muted">
                  Quantity will be set to:{" "}
                  {formatNumber(adjustFormData.adjustment_quantity)}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Reason for Adjustment</Form.Label>
              <Form.Control
                as="textarea"
                name="reason"
                rows={3}
                value={adjustFormData.reason}
                onChange={handleAdjustFormChange}
                placeholder="e.g., Stock take correction, Damaged goods, Return, etc."
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowAdjustModal(false)}
            >
              Cancel
            </Button>
            <Button variant="warning" type="submit" disabled={savingAdjustment}>
              {savingAdjustment ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Applying...
                </>
              ) : (
                "Apply Adjustment"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Locations;
